import { useCallback, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Loader2, Search } from 'lucide-react'
import { clientesService } from '@/services/clientesService'
import { financeService } from '@/services/financeService'
import { cn } from '@/utils/cn'

export type EmpresaOption = {
  id: string
  nome: string
  cnpj?: string
  email?: string
  razaoSocial?: string
}

type DealOption = {
  id: string
  nome: string
  etapa: string
  valor: number | null
}

type Props = {
  onSelect: (
    empresa: EmpresaOption,
    prefill: Record<string, string>,
    linkedDealId?: string,
  ) => void
  onClear: () => void
  /** Tema claro fixo (portal PJ / modal em fundo branco) */
  variant?: 'default' | 'portalLight'
}

function formatCnpj(cnpj: string) {
  const d = cnpj.replace(/\D/g, '')
  if (d.length !== 14) return cnpj
  return d.replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, '$1.$2.$3/$4-$5')
}

export function FinanceFormEmpresaSection({ onSelect, onClear, variant = 'default' }: Props) {
  const isPortalLight = variant === 'portalLight'
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<EmpresaOption[]>([])
  const [deals, setDeals] = useState<DealOption[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [selectedEmpresa, setSelectedEmpresa] = useState<EmpresaOption | null>(null)
  const [selectedDealId, setSelectedDealId] = useState('')
  const [dropdownRect, setDropdownRect] = useState<DOMRect | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout>>()
  const wrapperRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  const updateDropdownRect = useCallback(() => {
    if (inputRef.current) setDropdownRect(inputRef.current.getBoundingClientRect())
  }, [])

  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [])

  useEffect(() => {
    if (!open) {
      setDropdownRect(null)
      return
    }
    updateDropdownRect()
    window.addEventListener('scroll', updateDropdownRect, true)
    window.addEventListener('resize', updateDropdownRect)
    return () => {
      window.removeEventListener('scroll', updateDropdownRect, true)
      window.removeEventListener('resize', updateDropdownRect)
    }
  }, [open, results, updateDropdownRect])

  const search = useCallback(async (q: string) => {
    if (q.trim().length < 2) {
      setResults([])
      setOpen(false)
      return
    }
    setLoading(true)
    try {
      const res = await clientesService.list({ page: 1, limit: 20, search: q.trim() })
      setResults(
        (res.data ?? []).map((c) => ({
          id: c.id,
          nome: c.nome,
          cnpj: c.cnpj ?? undefined,
          razaoSocial: c.razaoSocial ?? undefined,
          email: c.email ?? undefined,
        })),
      )
      setOpen(true)
    } catch {
      setResults([])
    } finally {
      setLoading(false)
    }
  }, [])

  const loadEmpresaDeals = async (empresaId: string) => {
    try {
      const data = await financeService.prefillEmpresa(empresaId)
      setDeals(data.deals)
      onSelect(
        { id: data.empresa.id, nome: data.empresa.nome, cnpj: data.empresa.cnpj ?? undefined },
        data.prefill,
      )
    } catch {
      setDeals([])
    }
  }

  const handleChange = (value: string) => {
    setQuery(value)
    setSelectedEmpresa(null)
    setSelectedDealId('')
    setDeals([])
    onClear()
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => void search(value), 350)
  }

  const handlePick = async (empresa: EmpresaOption) => {
    setQuery(empresa.nome)
    setSelectedEmpresa(empresa)
    setOpen(false)
    setSelectedDealId('')
    await loadEmpresaDeals(empresa.id)
  }

  const handleDealChange = async (dealId: string) => {
    setSelectedDealId(dealId)
    if (!dealId || !selectedEmpresa) return
    try {
      const data = await financeService.prefillDeal(dealId)
      onSelect(selectedEmpresa, data.prefill, data.linkedDealId)
    } catch {
      /* keep empresa prefill */
    }
  }

  const shellClass = isPortalLight
    ? 'rounded-lg border border-[#dfe1e6] bg-[#f8fafc] p-3'
    : 'rounded-lg border border-border bg-bg-secondary/50 p-3'

  const inputClass = isPortalLight
    ? 'w-full rounded-lg border border-[#dfe1e6] bg-white py-2 pl-9 pr-3 text-sm text-[#172b4d] placeholder:text-[#94a3b8] focus:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/20'
    : 'w-full rounded-lg border border-border bg-bg-primary py-2 pl-9 pr-3 text-sm'

  const dropdown =
    open &&
    results.length > 0 &&
    dropdownRect &&
    createPortal(
      <ul
        style={{
          position: 'fixed',
          top: dropdownRect.bottom + 4,
          left: dropdownRect.left,
          width: dropdownRect.width,
          zIndex: 10000,
        }}
        className={cn(
          'max-h-56 overflow-y-auto rounded-lg border shadow-xl',
          isPortalLight
            ? 'border-[#dfe1e6] bg-white'
            : 'border-border bg-bg-primary shadow-lg',
        )}
      >
        {results.map((r) => (
          <li key={r.id} className='border-b border-[#eef2f7] last:border-b-0'>
            <button
              type='button'
              className={cn(
                'w-full px-3 py-2.5 text-left text-sm transition-colors',
                isPortalLight
                  ? 'hover:bg-[#f0fdf4] text-[#172b4d]'
                  : 'hover:bg-bg-secondary',
              )}
              onClick={() => void handlePick(r)}
            >
              <span className='block font-medium leading-snug'>{r.nome}</span>
              {r.cnpj && (
                <span className='mt-0.5 block font-mono text-xs text-[#6b778c]'>
                  {formatCnpj(r.cnpj)}
                </span>
              )}
              {r.razaoSocial && r.razaoSocial !== r.nome && (
                <span className='mt-0.5 block text-xs text-[#94a3b8]'>{r.razaoSocial}</span>
              )}
            </button>
          </li>
        ))}
      </ul>,
      document.body,
    )

  return (
    <div ref={wrapperRef} className={shellClass}>
      <label className={cn('block text-sm font-medium', isPortalLight ? 'text-[#172b4d]' : 'text-text-primary')}>
        Cliente Agrada
      </label>
      <p className={cn('mb-2 text-xs', isPortalLight ? 'text-[#6b778c]' : 'text-text-muted')}>
        Busque entre os clientes ativos da Agrada. Selecione a empresa e, se aplicável, o deal para
        pré-preencher contatos e valores.
      </p>
      <div className='relative'>
        <Search
          size={14}
          className={cn(
            'absolute left-3 top-1/2 -translate-y-1/2',
            isPortalLight ? 'text-[#94a3b8]' : 'text-text-muted',
          )}
        />
        {loading && (
          <Loader2
            size={14}
            className={cn(
              'absolute right-3 top-1/2 -translate-y-1/2 animate-spin',
              isPortalLight ? 'text-[#94a3b8]' : 'text-text-muted',
            )}
          />
        )}
        <input
          ref={inputRef}
          type='search'
          className={inputClass}
          placeholder='Nome, razão social ou CNPJ…'
          value={query}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => {
            if (results.length > 0) setOpen(true)
          }}
        />
        {dropdown}
      </div>
      {selectedEmpresa && deals.length > 0 && (
        <label className='mt-3 block text-sm'>
          <span
            className={cn(
              'mb-1 block font-medium',
              isPortalLight ? 'text-[#172b4d]' : 'text-text-primary',
            )}
          >
            Deal vinculado (opcional)
          </span>
          <select
            className={inputClass}
            value={selectedDealId}
            onChange={(e) => void handleDealChange(e.target.value)}
          >
            <option value=''>Sem deal específico</option>
            {deals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.nome} ({d.etapa})
              </option>
            ))}
          </select>
        </label>
      )}
      {selectedEmpresa && (
        <p className={cn('mt-2 text-xs', isPortalLight ? 'text-emerald-700' : 'text-accent')}>
          Vinculado: {selectedEmpresa.nome}
        </p>
      )}
    </div>
  )
}
